import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrencyCOP } from '../utils/formatters';

import { computeCollectionAmounts } from '../utils/payments';
import { walletService } from '../services/api';
import PaymentVerificationModal from './PaymentVerificationModal';

const PickupPaymentModal = ({ isOpen, order, onClose, onConfirm, cashSessionOpen = true }) => {
  const [payments, setPayments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null); // State for image preview modal
  const [isImageEnlarged, setIsImageEnlarged] = useState(false); // Controls fullscreen overlay

  // Credit Note (Nota Crédito) State
  const [creditNoteResults, setCreditNoteResults] = useState([]);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [searchingCreditNotes, setSearchingCreditNotes] = useState(false);
  const [creditNoteError, setCreditNoteError] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [pendingMovements, setPendingMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [movementSearch, setMovementSearch] = useState('');

  // Verification Modal State
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationConfig, setVerificationConfig] = useState(null); // { remaining, totalRegistered } // Estado global de búsqueda (podría ser local por row, pero simplifiquemos)

  // Si quisiéramos búsqueda independiente por fila, necesitaríamos un mapa.
  // Dado que es poco probable tener muchos bloques de transferencia simultáneos, usaremos un search global o local en el render.
  // Mejor usemos un componente extraido o simplemente estado local si refactorizamos.
  // Por ahora, asumamos que el search es efímero en el render. Wait, controlled input needs state.
  // Let's add 'tempSearch' to payment object if we want per-row search, OR just a global search state if we assume one main action.
  // Given user flow: "Recibir Pago", usually one big transfer. Let's start with global search state, or reset it.

  // Fetch movements
  useEffect(() => {
    if (isOpen) {
      setLoadingMovements(true);
      walletService.getBancolombiaMovements({ status: 'pending', limit: 500 })
        .then(response => {
          // La API devuelve { success: true, data: [...] }
          // Nos aseguramos de extraer el array
          const list = Array.isArray(response) ? response : (response.data || []);
          setPendingMovements(list);
        })
        .catch(err => console.error('Error loading movements', err))
        .finally(() => setLoadingMovements(false));
    }
  }, [isOpen]);

  // Fetch Existing Payment Evidences
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [existingPayments, setExistingPayments] = useState([]);
  const [linkedMovementsHistory, setLinkedMovementsHistory] = useState([]); // NEW: Store linked movements for display
  const [siigoVouchers, setSiigoVouchers] = useState([]); // NEW: Store RCs from Siigo
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Auto-select first image if available
  useEffect(() => {
    if (previewImage) return;

    if (existingEvidences && existingEvidences.length > 0) {
      setPreviewImage(existingEvidences[0]);
      return;
    }

    try {
      for (const p of payments) {
        if (p.files && p.files.length > 0) {
          setPreviewImage(p.files[0]);
          return;
        }
      }
    } catch (e) { }
  }, [existingEvidences, payments]);

  useEffect(() => {
    if (isOpen && order?.id) {
      // RESET STATE: Clear artifacts from previous orders to prevent ghosting
      setPreviewImage(null);
      setIsImageEnlarged(false);
      setExistingEvidences([]);
      // Load payment evidences (includes delivery evidence from messenger)
      walletService.getPaymentEvidences(order.id)
        .then(response => {
          // Payment evidences from Cartera uploads
          const list = Array.isArray(response) ? response : (response.data || []);
          const urls = list.map(e => e.file_path);

          // Delivery evidence from messenger (payment proofs, delivery photos)
          const deliveryEvs = response?.delivery_evidence || [];
          // Prioritize payment-type evidence, then photos
          const deliveryUrls = deliveryEvs.map(e => e.file_path).filter(Boolean);

          // Merge: delivery payment proofs first, then cartera evidences
          setExistingEvidences([...deliveryUrls, ...urls]);
        })
        .catch(err => console.error('Error loading evidences', err));

      // Load existing payment transactions and linked movements
      setLoadingPayments(true);

      Promise.all([
        walletService.getOrderPayments(order.id).catch(() => ({ data: [] })),
        walletService.getLinkedPayments(order.id).catch(() => ({ data: [] }))
      ])
        .then(([transactionsResponse, linkedResponse]) => {
          const transactions = Array.isArray(transactionsResponse) ? transactionsResponse : (transactionsResponse.data || []);
          const linked = Array.isArray(linkedResponse) ? linkedResponse : (linkedResponse.data || []);

          // 1. Set existing payments (ONLY confirmed transactions)
          setExistingPayments(transactions.map(t => ({
            id: t.id,
            amount: t.amount,
            payment_method: t.payment_method || t.method,
            created_at: t.created_at,
            source: 'transaction'
          })));

          // NEW: Store linked movements for informative display
          setLinkedMovementsHistory(linked.filter(l => l.pedido_id === order.id));

          // 2. CRITICAL FIX: getLinkedPayments returns movements ALREADY linked to this order
          // These should NEVER be proposed again for linking
          // Only propose a movement if it's linked BUT not yet in a confirmed transaction
          // AND the movement is NOT already fully applied to this order

          // Actually, simpler logic: if getLinkedPayments returns movements for this order,
          // they are ALREADY linked. We should NOT propose them.
          // Only exception: if they were linked but transaction failed to create.

          // For safety, let's not auto-propose ANY linked movements
          // User can manually search and add if needed
          const pendingLinked = []; // Disable auto-proposal entirely

          /*
          // OLD BUGGY LOGIC - kept for reference:
          const pendingLinked = linked.filter(l => {
            if (!l.pedido_id || l.pedido_id !== order.id) return false;
            const alreadyInTransaction = transactions.some(t => {
              if (!t.bancolombia_movement_id) return false;
              const ids = String(t.bancolombia_movement_id).split(',');
              return ids.includes(String(l.id));
            });
            return !alreadyInTransaction;
          });
          */

          // 3. If there are pending linked movements, propose the first one in the form
          if (pendingLinked.length > 0) {
            const proposal = pendingLinked[0];

            // Update the form state to pre-fill with this movement
            const initialMethod = 'transferencia';
            setPayments([{
              method: initialMethod,
              amount: proposal.valor,
              files: [],
              linkedMovements: [proposal],
              searchQuery: proposal.descripcion,
              electronicType: 'bold'
            }]);
          }
        })
        .catch(err => {
          console.error('Error loading payments:', err);
          setExistingPayments([]);
        })
        .finally(() => {
          setLoadingPayments(false);
        });

      // Load Siigo vouchers (RCs) for this order
      walletService.getOrderVouchers(order.id)
        .then(response => {
          const vouchers = response?.data || [];
          console.log('📋 Loaded RCs for order:', vouchers);
          setSiigoVouchers(vouchers);
        })
        .catch(err => {
          console.warn('Could not fetch vouchers:', err);
          setSiigoVouchers([]);
        });
    }
  }, [isOpen, order?.id]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (dateString.includes('T')) return dateString.split('T')[0];
    return d.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  useEffect(() => {
    if (isOpen && order) {
      // Calcular saldo pendiente inicial
      const paid = Number(order.paid_amount || 0);
      const cashRegistered = Number(order.total_cash_registered || 0);
      const total = Number(order.total_amount || 0) + Number(order.internal_orders_total || 0);
      const pending = Math.max(0, total - paid - cashRegistered);

      // Determinar método de pago inicial basándose en el payment_method del pedido
      let initialMethod = ''; // Default: sin preselección
      const orderMethod = String(order.payment_method || '').toLowerCase().trim();

      // Pre-seleccionar método de pago desde la orden para flujo Cartera
      if (orderMethod.includes('mercado') || orderMethod === 'mercado_pago') {
        initialMethod = 'mercado_pago';
      } else if (orderMethod.includes('transfer') || orderMethod === 'transferencia') {
        initialMethod = 'transferencia';
      } else if (orderMethod.includes('tarjeta') || orderMethod === 'tarjeta_credito' || orderMethod === 'debito') {
        initialMethod = 'tarjeta_credito';
      } else if (orderMethod.includes('electronico') || orderMethod === 'pago_electronico') {
        initialMethod = 'pago_electronico';
      } else if (orderMethod.includes('efectivo') || orderMethod === 'contado' || orderMethod === 'contraentrega') {
        initialMethod = 'efectivo';
      } else if (orderMethod.includes('credito') || orderMethod === 'cliente_credito') {
        initialMethod = 'efectivo'; // Crédito no se registra como pago, default a efectivo
      }

      // Determinar tipo de pago electrónico (bold, nequi, daviplata, etc.)
      const electronicType = String(order.electronic_payment_type || 'bold').toLowerCase().trim();

      setPayments([
        { method: initialMethod, amount: '', files: [], linkedMovements: [], searchQuery: '', electronicType }
      ]);
    }
  }, [isOpen, order]);

  const addPaymentMethod = () => {
    // Calcular saldo restante para auto-completar el nuevo medio
    const totalPayments = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const paid = Number(order.paid_amount || 0);
    const total = (Number(order.total_amount || 0) + Number(order.internal_orders_total || 0));
    const pending = Math.max(0, total - paid - totalPayments);

    setPayments([...payments, { method: 'transferencia', amount: pending, files: [], linkedMovements: [], searchQuery: '', electronicType: 'bold' }]);
  };

  const removePaymentMethod = (index) => {
    if (payments.length > 1) {
      // If removing a nota_credito row, clear NC state
      if (payments[index].method === 'nota_credito') {
        setSelectedCreditNote(null);
        setCreditNoteResults([]);
        setCreditNoteError(null);
      }
      const newPayments = [...payments];
      newPayments.splice(index, 1);
      setPayments(newPayments);
    }
  };

  const updatePayment = (index, field, value) => {
    const newPayments = [...payments];
    newPayments[index][field] = value;
    // Auto-fill remaining balance when selecting saldo_a_favor
    if (field === 'method' && value === 'saldo_a_favor') {
      const otherPaymentsTotal = newPayments.reduce((sum, p, i) => i !== index ? sum + (Number(p.amount) || 0) : sum, 0);
      const orderTotal = (Number(order?.total_amount || 0) + Number(order?.internal_orders_total || 0));
      const autoAmount = Math.max(0, orderTotal - Number(order?.paid_amount || 0) - otherPaymentsTotal);
      newPayments[index].amount = autoAmount;
    }
    // Reset credit note state when changing method away from nota_credito
    if (field === 'method' && value !== 'nota_credito') {
      setSelectedCreditNote(null);
      setCreditNoteResults([]);
      setCreditNoteError(null);
    }
    // Auto-fill when selecting nota_credito and a credit note is already selected
    if (field === 'method' && value === 'nota_credito') {
      if (selectedCreditNote) {
        const orderTotal = (Number(order?.total_amount || 0) + Number(order?.internal_orders_total || 0));
        newPayments[index].amount = Math.min(selectedCreditNote.total, orderTotal - Number(order?.paid_amount || 0));
      }
    }
    setPayments(newPayments);
  };

  const handleFileChange = (index, e) => {
    const newFiles = Array.from(e.target.files || []);
    const currentFiles = [...payments[index].files];
    updatePayment(index, 'files', [...currentFiles, ...newFiles]);
  };

  const removeFile = (pIndex, fIndex) => {
    const newFiles = [...payments[pIndex].files];
    newFiles.splice(fIndex, 1);
    updatePayment(pIndex, 'files', newFiles);
  };

  const [activePasteIndex, setActivePasteIndex] = useState(null);

  // Auto-activar el primer campo no efectivo al cargar o cambiar pagos
  useEffect(() => {
    const firstNonCashIndex = payments.findIndex(p => p.method !== 'efectivo');
    if (firstNonCashIndex !== -1) {
      setActivePasteIndex(firstNonCashIndex);
    } else {
      setActivePasteIndex(null);
    }
  }, [payments.length, payments.map(p => p.method).join(',')]);

  // Listener global para pegar
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      // Si no hay índice activo o el modal no está abierto, ignorar
      if (activePasteIndex === null || !isOpen) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault(); // Prevenir pegado default si es imagen
            const pastedFile = new File([file], `pasted-image-${Date.now()}-${i}.png`, { type: file.type });
            const currentFiles = [...payments[activePasteIndex].files];

            const newPayments = [...payments];
            newPayments[activePasteIndex].files = [...currentFiles, pastedFile];
            setPayments(newPayments);

            toast.success('Imagen pegada correctamente');
            return; // Solo pegar una a la vez para evitar duplicados si hay múltiples items
          }
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [activePasteIndex, isOpen, payments]);



  if (!isOpen || !order) return null;

  const totalRegistered = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  // CRITICAL: Use order.paid_amount directly from DB instead of summing existingPayments
  // This prevents duplicate counting when movements are linked multiple times in the system
  const previouslyPaid = Number(order.paid_amount || 0);
  const remaining = (Number(order.total_amount || 0) + Number(order.internal_orders_total || 0)) - previouslyPaid - totalRegistered;

  const confirmSubmission = async (cancelOrder = false) => {
    try {
      setSubmitting(true);
      setShowVerificationModal(false); // Close modal if open
      setShowCancelConfirm(false); // Close cancel confirm if open

      // Only include NC data if nota_credito is actually selected as payment method
      const hasNCMethod = payments.some(p => p.method === 'nota_credito');

      await onConfirm({
        orderId: order.id,
        cancelOrder: cancelOrder === true && hasNCMethod, // Defense: only allow cancel with NC
        creditNoteId: hasNCMethod ? (selectedCreditNote?.id || null) : null,
        creditNoteName: hasNCMethod ? (selectedCreditNote?.name || null) : null,
        payments: payments.map(p => ({
          method: p.method === 'pago_electronico' ? `${p.method}_${p.electronicType || 'bold'}` : p.method,
          amount: Number(p.amount),
          files: p.files,
          linkedMovementIds: (p.linkedMovements || []).map(m => m.id)
        }))
      });
      setSubmitting(false);
      onClose();
    } catch (err) {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let i = 0; i < payments.length; i++) {
      const p = payments[i];
      if (p.method !== 'efectivo' && p.method !== 'efectivo_ruta_domicilio' && p.method !== 'saldo_a_favor' && p.method !== 'nota_credito' && p.files.length === 0 && (p.linkedMovements || []).length === 0 && existingEvidences.length === 0) {
        toast.error(`El método ${p.method} requiere al menos un soporte de pago (adjunto, movimiento vinculado o soporte histórico existente).`);
        return;
      }
    }

    // Validación de tolerancia con Modal Rico
    if (Math.abs(remaining) > 100) {
      setVerificationConfig({ remaining, totalRegistered });
      setShowVerificationModal(true);
      return;
    }

    // Check if nota_credito covers 100% of order → offer cancel
    const hasNC = payments.some(p => p.method === 'nota_credito');
    if (hasNC && selectedCreditNote) {
      const orderTotal = (Number(order?.total_amount || 0) + Number(order?.internal_orders_total || 0)) - Number(order?.paid_amount || 0);
      const ncTotal = payments.filter(p => p.method === 'nota_credito').reduce((s, p) => s + Number(p.amount || 0), 0);
      if (ncTotal >= orderTotal - 100) {
        setShowCancelConfirm(true);
        return;
      }
    }

    // Si no hay discrepancia, enviar directo
    confirmSubmission();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white w-full max-w-6xl rounded-xl shadow-2xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0 bg-white z-10">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center mb-1">
              <Icons.Wallet className="w-6 h-6 mr-2 text-emerald-600" />
              {order.delivery_method === 'recoge_posterior' ? 'Aplicar Pago (Recogida Posterior)' : 'Aplicar Pago'}
            </h3>
            <div className="flex items-center flex-wrap gap-4 mt-2">
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Pedido</span>
                <span className="text-4xl font-black text-blue-900 tracking-tight">{order.order_number}</span>
              </div>
              <div className="text-2xl font-bold text-gray-800 border-l-2 border-gray-300 pl-4 py-1 uppercase">
                {order.customer_name}
              </div>
              {order.siigo_public_url && (
                <a
                  href={order.siigo_public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors border border-blue-200 ml-2"
                  title="Ver Factura SIIGO"
                >
                  <Icons.FileText className="w-4 h-4" />
                  Ver Factura
                </a>
              )}
              {/* Delivery & Payment method badges */}
              {order.delivery_method && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium border border-gray-200 ml-2">
                  <Icons.Truck className="w-3.5 h-3.5" />
                  {(order.delivery_method || '').replace(/_/g, ' ')}
                </span>
              )}
              {order.payment_method && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium border border-blue-200 ml-1">
                  <Icons.CreditCard className="w-3.5 h-3.5" />
                  {(order.payment_method || '').replace(/_/g, ' ')}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-1">Por Pagar</div>
              <div className="text-2xl font-black text-orange-600">
                {formatCurrencyCOP((Number(order.total_amount || 0) + Number(order.internal_orders_total || 0)) - (order.paid_amount || 0))}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full">
              <Icons.X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row">

            {/* LEFT SIDEBAR - SUMMARY & INFO (50% - Expanded for Image View) */}
            <div className="w-full md:w-1/2 bg-slate-50 border-r border-gray-200 p-0 flex flex-col overflow-hidden shrink-0">

              {/* IMAGE PREVIEW AREA (Top Priority) */}
              <div className="flex-1 overflow-hidden bg-gray-900 relative flex items-center justify-center min-h-[400px]">
                {previewImage ? (
                  <>
                    <img
                      src={
                        (typeof previewImage === 'string'
                          ? (previewImage.startsWith('http') ? previewImage : `/api/${previewImage.replace(/^\//, '')}`)
                          : URL.createObjectURL(previewImage))
                      }
                      alt="Payment Proof"
                      className="max-w-full max-h-full object-contain cursor-zoom-in hover:opacity-95 transition-opacity"
                      onClick={() => setIsImageEnlarged(true)}
                    />
                    {/* Overlay controls if needed */}
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                      {/* If multiple images, dots or thumbnails could go here */}
                    </div>
                  </>
                ) : (
                  <div className="text-gray-500 flex flex-col items-center p-8 text-center">
                    <Icons.Image className="w-16 h-16 mb-4 opacity-20" />
                    <p className="text-sm font-medium">Sin comprobante seleccionado</p>
                    <p className="text-xs opacity-60 mt-1">Sube o pega una imagen para verla aquí</p>
                  </div>
                )}
              </div>

              {/* SMALL SUMMARY BELOW IMAGE (Scrollable if needed) */}
              <div className="h-1/3 min-h-[200px] overflow-y-auto bg-white border-t border-gray-200 p-6 custom-scrollbar">

                {/* Financial Summary Card (Compact) */}
                <div className="mb-6">
                  <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2 flex items-center">
                    <Icons.Calculator className="w-3 h-3 mr-2" /> Resumen Financiero
                  </h4>
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100 flex justify-between items-center">
                    <div className="text-xs text-emerald-800">
                      <span className="block opacity-70">Total Pedido</span>
                      <span className="font-bold text-sm">{formatCurrencyCOP(Number(order.total_amount || 0) + Number(order.internal_orders_total || 0))}</span>
                    </div>
                    <div className="text-right">
                      <span className="block text-xs text-emerald-800 opacity-70">Por Pagar</span>
                      <span className={`text-xl font-bold ${remaining <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                        {formatCurrencyCOP(remaining)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Existing Payments & Linked Movements (Informative) */}
                {(existingPayments.length > 0 || (Array.isArray(walletService.getLinkedPayments) && false)) && (
                  <div className="mb-6">
                    <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2 flex items-center">
                      <Icons.Receipt className="w-3 h-3 mr-2" /> Pagos Registrados (Histórico)
                    </h4>
                    <div className="space-y-2">
                      {existingPayments.map((payment, idx) => {
                        // Parse siigo_payment_info to find RC for this payment
                        let rcInfo = null;
                        try {
                          const siigoPaymentInfo = order?.siigo_payment_info;
                          if (siigoPaymentInfo) {
                            const rcArray = typeof siigoPaymentInfo === 'string'
                              ? JSON.parse(siigoPaymentInfo)
                              : siigoPaymentInfo;

                            if (Array.isArray(rcArray) && rcArray.length > 0) {
                              // Match by payment method (case insensitive)
                              rcInfo = rcArray.find(rc =>
                                rc.payment_method?.toLowerCase() === payment.payment_method?.toLowerCase()
                              ) || rcArray[0]; // Fallback to first RC if only one exists
                            }
                          }
                        } catch (e) {
                          console.warn('Error parsing siigo_payment_info:', e);
                        }

                        return (
                          <div key={idx} className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-xs font-bold text-blue-900 capitalize">
                                  {payment.payment_method || 'N/A'}
                                  {rcInfo && (
                                    <span className="ml-2 text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                      {rcInfo.name}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-blue-600 mt-0.5">
                                  {payment.created_at ? new Date(payment.created_at).toLocaleDateString('es-CO', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  }) : 'Fecha desconocida'}
                                </div>
                              </div>
                              <div className="text-sm font-bold text-blue-900">
                                {formatCurrencyCOP(payment.amount)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="flex justify-between items-center pt-2 px-1 border-t border-blue-200 border-dashed">
                        <span className="text-xs font-medium text-blue-800">Total Pagado:</span>
                        <span className="text-sm font-bold text-blue-700">
                          {formatCurrencyCOP(existingPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Linked Bank Movements (Informative) */}
                {linkedMovementsHistory.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-xs font-bold text-teal-700 uppercase tracking-wider mb-2 flex items-center">
                      <Icons.Banknote className="w-3 h-3 mr-2" /> Movimientos Bancolombia Vinculados
                    </h4>
                    <div className="space-y-2">
                      {linkedMovementsHistory.map((movement, idx) => (
                        <div key={idx} className="bg-teal-50 rounded-lg p-3 border border-teal-100">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] text-teal-600 truncate">
                                {movement.descripcion || 'Sin descripción'}
                              </div>
                              <div className="text-xs text-teal-500 mt-0.5">
                                {movement.fecha ? formatDate(movement.fecha) : 'Fecha desconocida'}
                              </div>
                            </div>
                            <div className="text-sm font-bold text-teal-900 shrink-0">
                              {formatCurrencyCOP(movement.valor)}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-2 px-1 border-t border-teal-200 border-dashed">
                        <span className="text-xs font-medium text-teal-800">Total Vinculado:</span>
                        <span className="text-sm font-bold text-teal-700">
                          {formatCurrencyCOP(linkedMovementsHistory.reduce((sum, m) => sum + (Number(m.valor) || 0), 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Siigo RCs (Recibos de Caja) */}
                {siigoVouchers.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-xs font-bold text-purple-700 uppercase tracking-wider mb-2 flex items-center">
                      <Icons.FileText className="w-3 h-3 mr-2" /> RCs Creados en Siigo
                    </h4>
                    <div className="space-y-2">
                      {siigoVouchers.map((voucher, idx) => (
                        <div key={idx} className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold text-purple-900 truncate">
                                {voucher.name || voucher.number || 'RC sin número'}
                              </div>
                              <div className="text-[10px] text-purple-600 mt-0.5">
                                {voucher.date ? formatDate(voucher.date) : 'Fecha desconocida'}
                              </div>
                            </div>
                            <div className="text-sm font-bold text-purple-900 shrink-0">
                              {formatCurrencyCOP(voucher.total || 0)}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-2 px-1 border-t border-purple-200 border-dashed">
                        <span className="text-xs font-medium text-purple-800">Total RCs:</span>
                        <span className="text-sm font-bold text-purple-700">
                          {formatCurrencyCOP(siigoVouchers.reduce((sum, v) => sum + (Number(v.total) || 0), 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Soportes Existentes (Mini Thumbnails for Selection) */}
                {(existingEvidences.length > 0 || payments.some(p => p.files.length > 0)) && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                      <Icons.Image className="w-3 h-3 mr-2" /> Galería de Soportes
                    </h4>
                    <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
                      {/* Existing Evidences */}
                      {existingEvidences.map((url, idx) => (
                        <div
                          key={`exist-${idx}`}
                          onClick={() => setPreviewImage(url)}
                          className={`w-16 h-16 shrink-0 rounded-lg border-2 cursor-pointer overflow-hidden relative ${previewImage === url ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <img
                            src={url.startsWith('http') ? url : `/api/${url.replace(/^\//, '')}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              if (!e.target.src.includes('/api/public/evidence')) {
                                e.target.src = `/api/public/evidence/${url.split('/').pop()}/content`;
                              }
                            }}
                          />
                        </div>
                      ))}
                      {/* New Uploads */}
                      {payments.flatMap(p => p.files).map((file, idx) => (
                        <div
                          key={`new-${idx}`}
                          onClick={() => setPreviewImage(file)}
                          className={`w-16 h-16 shrink-0 rounded-lg border-2 cursor-pointer overflow-hidden relative ${previewImage === file ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" />
                          {/* Badge "Nuevo" */}
                          <span className="absolute top-0 right-0 bg-green-500 w-2 h-2 rounded-full border border-white"></span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Order Details (Collapsed/Compact) */}
                <div className="text-xs text-gray-500 border-t pt-4">
                  <p className="flex justify-between mb-1">
                    <span>Método Pago:</span>
                    <span className="font-medium text-gray-700 capitalize">{order.payment_method}</span>
                  </p>
                  <p className="flex justify-between mb-1">
                    <span>Entrega:</span>
                    <span className="font-medium text-gray-700 capitalize">{order.delivery_method}</span>
                  </p>
                </div>

              </div>
            </div>

            {/* RIGHT MAIN - PAYMENTS INPUTS (50%) */}
            <div className="w-full md:w-1/2 p-6 overflow-y-auto bg-white custom-scrollbar">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-base font-bold text-gray-800 flex items-center">
                  <Icons.CreditCard className="w-4 h-4 mr-2 text-emerald-600" />
                  Medios de Pago
                </h4>
                <button
                  type="button"
                  onClick={addPaymentMethod}
                  className="text-xs flex items-center bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition-colors font-medium"
                >
                  <Icons.Plus className="w-3 h-3 mr-1" />
                  Agregar otro medio
                </button>
              </div>

              <div className="space-y-6">
                {payments.map((payment, index) => (
                  <div key={index} className="p-5 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow relative group">
                    {payments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePaymentMethod(index)}
                        className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition-colors p-1"
                        title="Eliminar este pago"
                      >
                        <Icons.Trash2 className="w-4 h-4" />
                      </button>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Método</label>
                        <div className="relative">
                          <select
                            value={payment.method}
                            onChange={(e) => updatePayment(index, 'method', e.target.value)}
                            className="w-full pl-3 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent cursor-pointer hover:bg-gray-100 transition-colors appearance-none"
                          >
                            <option value="" disabled>Seleccionar método...</option>
                            <option value="efectivo">💵 Efectivo</option>
                            {(order?.delivery_method || '').toLowerCase().includes('domicilio') && (
                              <option value="efectivo_ruta_domicilio">🚗 Efectivo Ruta Domicilio</option>
                            )}
                            <option value="transferencia">🏦 Transferencia Bancaria</option>
                            {/* HIDDEN Feb 10, 2026: Confusing for users, use Pago Electrónico instead */}
                            {/* <option value="tarjeta_credito">💳 Datáfono / Tarjeta</option> */}
                            <option value="pago_electronico">💳 Tarjeta Crédito/Débito</option>
                            <option value="mercado_pago">💙 Mercado Pago</option>
                            <option value="saldo_a_favor">💎 Saldo a Favor (Siigo)</option>
                            <option value="nota_credito">📝 Nota Crédito (NC)</option>
                          </select>
                          <Icons.ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Monto a Registrar</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2.5 text-gray-400 font-medium">$</span>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={payment.amount}
                            onChange={(e) => updatePayment(index, 'amount', e.target.value)}
                            className="w-full pl-7 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Tipo de Pago Electrónico (Solo cuando método es pago_electronico) */}
                    {payment.method === 'pago_electronico' && (
                      <div className="mb-4">
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Tipo de Pago Electrónico</label>
                        <div className="relative">
                          <select
                            value={payment.electronicType || 'bold'}
                            onChange={(e) => updatePayment(index, 'electronicType', e.target.value)}
                            className="w-full pl-3 pr-8 py-2.5 bg-purple-50 border border-purple-200 rounded-lg text-sm font-medium text-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer hover:bg-purple-100 transition-colors appearance-none"
                          >
                            <option value="bold">📱 Bold</option>
                            <option value="mercadopago">💙 Mercadopago</option>
                          </select>
                          <Icons.ChevronDown className="absolute right-3 top-3 w-4 h-4 text-purple-400 pointer-events-none" />
                        </div>
                      </div>
                    )}

                    {/* Conciliación Bancaria Online (Solo Transferencia) */}
                    {payment.method === 'transferencia' && (
                      <div className="mt-4 mb-4">
                        <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100">
                          <div className="flex justify-between items-center mb-3">
                            <label className="text-xs font-bold text-indigo-700 uppercase flex items-center">
                              <Icons.Link className="w-3 h-3 mr-1.5" />
                              Conciliación Automática
                            </label>
                            {(payment.linkedMovements || []).length > 0 && (
                              <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold border border-emerald-200 flex items-center shadow-sm">
                                <Icons.CheckCircle2 className="w-3 h-3 mr-1" /> {(payment.linkedMovements || []).length} Vinculados
                              </span>
                            )}
                          </div>

                          {/* LISTA DE VINCULADOS */}
                          {(payment.linkedMovements || []).length > 0 && (
                            <div className="space-y-2 mb-4">
                              {payment.linkedMovements.map((lm, lmIdx) => (
                                <div key={lm.id} className="bg-white p-3 rounded-lg border border-emerald-200 shadow-sm flex justify-between items-center group/card relative">
                                  <div className="flex items-center gap-3">
                                    <div className="bg-emerald-100 p-2 rounded-full shrink-0">
                                      <Icons.Check className="w-4 h-4 text-emerald-700" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-bold text-gray-800">
                                        {formatCurrencyCOP(lm.valor)}
                                      </div>
                                      <div className="text-[10px] text-gray-500 max-w-[200px] truncate">
                                        {lm.descripcion}
                                      </div>
                                      <div className="text-xs text-gray-600 mt-0 font-bold">
                                        {formatDate(lm.fecha)}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newList = [...payment.linkedMovements];
                                      newList.splice(lmIdx, 1);
                                      updatePayment(index, 'linkedMovements', newList);
                                      // Recalcular total
                                      const newTotal = newList.reduce((sum, m) => sum + Number(m.valor), 0);
                                      updatePayment(index, 'amount', newTotal);
                                    }}
                                    className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"
                                    title="Desvincular este movimiento"
                                  >
                                    <Icons.X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              <div className="flex justify-between items-center pt-2 px-1 border-t border-indigo-100 border-dashed">
                                <span className="text-xs font-medium text-indigo-800">Total Vinculado:</span>
                                <span className="text-sm font-bold text-emerald-700">
                                  {formatCurrencyCOP(payment.linkedMovements.reduce((sum, m) => sum + Number(m.valor), 0))}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* BUSCADOR (Siempre visible para agregar más) */}
                          <div className="space-y-3">
                            <div className="relative group/search">
                              <Icons.Search className="absolute left-3 top-3 text-gray-400 group-focus-within/search:text-indigo-500 transition-colors w-4 h-4" />
                              <input
                                type="text"
                                placeholder={(payment.linkedMovements || []).length > 0 ? "Vincular otro movimiento..." : "Buscar movimiento en Bancolombia..."}
                                value={payment.searchQuery || ''}
                                onChange={(e) => updatePayment(index, 'searchQuery', e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-indigo-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm transition-all"
                              />
                            </div>

                            {(payment.searchQuery || '').length > 0 && (
                              <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-inner custom-scrollbar relative">
                                {loadingMovements && (
                                  <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center backdrop-blur-[1px]">
                                    <div className="flex flex-col items-center">
                                      <Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-600 mb-2" />
                                      <span className="text-xs text-indigo-700 font-medium">Buscando...</span>
                                    </div>
                                  </div>
                                )}

                                {(() => {
                                  const q = (payment.searchQuery || '').toLowerCase();
                                  // Filtrar movimientos ya vinculados en ESTA fila
                                  const linkedIds = new Set((payment.linkedMovements || []).map(m => m.id));

                                  const list = pendingMovements.filter(m =>
                                    !linkedIds.has(m.id) && ( // No mostrar ya vinculados
                                      m.valor.toString().includes(q) ||
                                      (m.descripcion || '').toLowerCase().includes(q)
                                    )
                                  );

                                  if (!loadingMovements && list.length === 0) {
                                    return (
                                      <div className="p-8 text-center flex flex-col items-center justify-center opacity-60">
                                        <Icons.SearchX className="w-8 h-8 text-gray-300 mb-2" />
                                        <p className="text-sm text-gray-500 font-medium">No se encontraron movimientos</p>
                                        <p className="text-xs text-gray-400">Intenta con otro valor o nombre</p>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div className="divide-y divide-gray-50">
                                      {list.map(m => (
                                        <div
                                          key={m.id}
                                          onClick={() => {
                                            const currentList = payment.linkedMovements || [];
                                            const newList = [...currentList, m];
                                            updatePayment(index, 'linkedMovements', newList);

                                            // Recalcular total sumando todos
                                            const newTotal = newList.reduce((sum, item) => sum + Number(item.valor), 0);
                                            updatePayment(index, 'amount', newTotal);

                                            // Limpiar búsqueda para permitir buscar el siguiente
                                            updatePayment(index, 'searchQuery', '');

                                            toast.success('Movimiento vinculado');
                                          }}
                                          className="p-3 hover:bg-indigo-50 cursor-pointer flex justify-between items-center transition-colors group/item"
                                        >
                                          <div>
                                            <div className="font-bold text-gray-700 text-sm group-hover/item:text-indigo-700 transition-colors">
                                              {formatCurrencyCOP(m.valor)}
                                            </div>
                                            <div className="text-xs text-gray-500 truncate w-48 mt-0.5">{m.descripcion}</div>
                                          </div>
                                          <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded font-bold">
                                            {formatDate(m.fecha)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Evidencia Upload */}
                    {/* Saldo a Favor Info Card */}
                    {payment.method === 'saldo_a_favor' && (
                      <div className="border-t border-gray-100 pt-4">
                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
                          <div className="flex items-start gap-3">
                            <div className="bg-purple-100 p-2 rounded-full shrink-0">
                              <Icons.Gem className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <h5 className="text-sm font-bold text-purple-900 mb-1">Aplicar Saldo a Favor</h5>
                              <p className="text-xs text-purple-700 leading-relaxed">
                                Se registrará el saldo a favor del cliente existente en Siigo para cubrir el monto restante de esta factura.
                                No se requiere soporte fotográfico.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Nota Crédito Search Card */}
                    {payment.method === 'nota_credito' && (
                      <div className="border-t border-gray-100 pt-4">
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="bg-amber-100 p-2 rounded-full shrink-0">
                              <Icons.FileText className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="flex-1">
                              <h5 className="text-sm font-bold text-amber-900 mb-1">Nota Crédito</h5>
                              <p className="text-xs text-amber-700 leading-relaxed">
                                Busca y selecciona una Nota Crédito de Siigo del cliente para aplicar a esta factura.
                              </p>
                            </div>
                          </div>

                          {/* Search Button */}
                          {!selectedCreditNote && (
                            <button
                              type="button"
                              disabled={searchingCreditNotes}
                              onClick={async () => {
                                setSearchingCreditNotes(true);
                                setCreditNoteError(null);
                                try {
                                  const result = await walletService.searchCreditNotes(order.id);
                                  if (result.success && result.data?.length > 0) {
                                    setCreditNoteResults(result.data);
                                    toast.success(`Se encontraron ${result.data.length} nota(s) crédito del cliente`);
                                  } else {
                                    setCreditNoteResults([]);
                                    setCreditNoteError('No se encontraron notas crédito para este cliente.');
                                  }
                                } catch (err) {
                                  console.error('Error searching credit notes:', err);
                                  setCreditNoteError(err.response?.data?.message || 'Error buscando notas crédito');
                                } finally {
                                  setSearchingCreditNotes(false);
                                }
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm disabled:opacity-50"
                            >
                              {searchingCreditNotes ? (
                                <><Icons.Loader2 className="w-4 h-4 animate-spin" /> Buscando en Siigo...</>
                              ) : (
                                <><Icons.Search className="w-4 h-4" /> Buscar Notas Crédito en Siigo</>
                              )}
                            </button>
                          )}

                          {/* Error */}
                          {creditNoteError && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
                              <Icons.AlertCircle className="w-4 h-4 shrink-0" />
                              {creditNoteError}
                            </div>
                          )}

                          {/* Results List */}
                          {creditNoteResults.length > 0 && !selectedCreditNote && (
                            <div className="mt-3 space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                              {creditNoteResults.map((cn) => (
                                <div
                                  key={cn.id}
                                  onClick={() => {
                                    setSelectedCreditNote(cn);
                                    const orderTotal = (Number(order?.total_amount || 0) + Number(order?.internal_orders_total || 0));
                                    const autoAmount = Math.min(cn.total, orderTotal - Number(order?.paid_amount || 0));
                                    updatePayment(index, 'amount', autoAmount);
                                    toast.success(`NC ${cn.name} seleccionada ($${cn.total.toLocaleString('es-CO')})`);
                                  }}
                                  className={`p-3 bg-white rounded-lg border ${cn.isCurrentInvoice ? 'border-green-300 hover:border-green-500' : 'border-amber-200 hover:border-amber-400'} cursor-pointer hover:shadow-md transition-all`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <div className="text-sm font-bold text-amber-900 flex items-center gap-2">
                                        {cn.name}
                                        {cn.isCurrentInvoice ? (
                                          <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Esta factura</span>
                                        ) : cn.invoiceName ? (
                                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">{cn.invoiceName}</span>
                                        ) : null}
                                      </div>
                                      <div className="text-xs text-amber-600 mt-0.5">
                                        {cn.date ? new Date(cn.date).toLocaleDateString('es-CO') : 'Sin fecha'}
                                      </div>
                                      {cn.observations && (
                                        <div className="text-[10px] text-gray-500 mt-1 truncate max-w-[250px]">{cn.observations}</div>
                                      )}
                                    </div>
                                    <div className="text-sm font-bold text-amber-900">
                                      {formatCurrencyCOP(cn.total)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Selected Credit Note */}
                          {selectedCreditNote && (
                            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <Icons.CheckCircle className="w-5 h-5 text-green-600" />
                                  <div>
                                    <div className="text-sm font-bold text-green-900">{selectedCreditNote.name}</div>
                                    <div className="text-xs text-green-700">Valor: {formatCurrencyCOP(selectedCreditNote.total)}</div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedCreditNote(null);
                                    updatePayment(index, 'amount', 0);
                                  }}
                                  className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"
                                  title="Quitar NC seleccionada"
                                >
                                  <Icons.X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {payment.method !== 'efectivo' && payment.method !== 'efectivo_ruta_domicilio' && payment.method !== 'saldo_a_favor' && payment.method !== 'nota_credito' && (
                      <div className="border-t border-gray-100 pt-4">
                        <label className="text-xs font-bold text-gray-500 mb-3 uppercase flex justify-between items-center">
                          <span>
                            Soportes / Evidencia <span className="text-red-500">*</span>
                            {payment.method === 'mercado_pago' && <span className="ml-2 text-blue-600 normal-case opacity-80">(Pega el sticker o comprobante)</span>}
                          </span>
                          {activePasteIndex === index && (
                            <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold animate-pulse">
                              LISTO PARA PEGAR (CTRL+V)
                            </span>
                          )}
                        </label>

                        <div className="flex gap-3 items-start">
                          <div
                            className={`flex-1 border-2 border-dashed rounded-lg p-4 transition-all cursor-pointer text-center relative group/upload
                             ${activePasteIndex === index
                                ? 'border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-100'
                                : 'border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
                              }`}
                            onClick={() => setActivePasteIndex(index)}
                          >
                            <div className="flex flex-col items-center justify-center" onClick={() => document.getElementById(`file-input-${index}`).click()}>
                              <div className={`p-2 rounded-full mb-2 ${activePasteIndex === index ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-gray-400 group-hover/upload:bg-gray-300'}`}>
                                <Icons.ImagePlus className="w-5 h-5" />
                              </div>
                              <span className={`text-sm font-medium ${activePasteIndex === index ? 'text-emerald-700' : 'text-gray-600'}`}>
                                {activePasteIndex === index ? 'Pega tu imagen (Ctrl + V)' : 'Clic para pegar o subir'}
                              </span>
                            </div>
                            <input
                              id={`file-input-${index}`}
                              type="file"
                              multiple
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleFileChange(index, e)}
                            />
                          </div>

                          {/* Miniaturas */}
                          {payment.files.length > 0 && (
                            <div className="flex flex-wrap gap-2 max-w-[50%]">
                              {payment.files.map((f, fIdx) => (
                                <div
                                  key={fIdx}
                                  className="relative group/file w-16 h-16 border rounded-lg bg-white p-0.5 shadow-sm cursor-zoom-in hover:ring-2 hover:ring-emerald-500 transition-all"
                                  onClick={() => setPreviewImage(f)}
                                >
                                  <img
                                    src={URL.createObjectURL(f)}
                                    alt="preview"
                                    className="w-full h-full object-cover rounded-md"
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); removeFile(index, fIdx); }}
                                    className="absolute -top-2 -right-2 bg-white text-red-500 border border-red-100 rounded-full p-0.5 shadow-md opacity-0 group-hover/file:opacity-100 hover:bg-red-50 transition-all scale-90 hover:scale-110"
                                  >
                                    <Icons.X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-between items-center shrink-0 z-20 shadow-[0_-5px_20px_rgba(0,0,0,0.02)]">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Total a Registrar:</span>
              <span className="text-xl font-bold text-gray-900 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-100">
                {formatCurrencyCOP(totalRegistered)}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-5 py-2.5 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting || !cashSessionOpen}
                className="px-6 py-2.5 text-sm font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 shadow-lg shadow-emerald-600/20 hover:shadow-emerald-600/30 transition-all flex items-center"
                title={!cashSessionOpen ? 'Debe abrir una caja para registrar pagos' : ''}
              >
                {submitting ? (
                  <>
                    <Icons.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Icons.CheckCircle className="w-4 h-4 mr-2" />
                    Registrar Pagos
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div >

      {/* Image Preview Modal */}
      {
        previewImage && isImageEnlarged && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4 backdrop-blur-md animate-in fade-in duration-200"
            onClick={() => setIsImageEnlarged(false)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors bg-white/10 p-3 rounded-full backdrop-blur-sm"
              onClick={() => setIsImageEnlarged(false)}
            >
              <Icons.X className="w-6 h-6" />
            </button>
            <img
              src={typeof previewImage === 'string' ? (previewImage.startsWith('http') ? previewImage : `/api/${previewImage.replace(/^\//, '')}`) : URL.createObjectURL(previewImage)}
              alt="Full size"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )
      }
      {/* Rich Verification Modal */}
      <PaymentVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onConfirm={() => confirmSubmission(false)}
        remaining={verificationConfig?.remaining || 0}
        totalRegistered={verificationConfig?.totalRegistered || 0}
      />

      {/* Cancel Order Confirmation Modal (when NC covers 100%) */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Icons.AlertTriangle className="w-5 h-5" />
                Nota Crédito Cubre el Total
              </h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">
                La Nota Crédito <strong>{selectedCreditNote?.name}</strong> cubre el 100% del saldo del pedido <strong>{order?.order_number}</strong>.
              </p>
              <p className="text-sm text-gray-600 mb-6">
                ¿Desea también <strong className="text-red-600">anular el pedido</strong> y retirarlo de la ruta?
              </p>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => confirmSubmission(true)}
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold text-sm disabled:opacity-50"
                >
                  {submitting ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.Trash2 className="w-4 h-4" />}
                  Registrar NC + Anular Pedido
                </button>
                <button
                  type="button"
                  onClick={() => confirmSubmission(false)}
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-bold text-sm disabled:opacity-50"
                >
                  {submitting ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.CheckCircle className="w-4 h-4" />}
                  Solo Registrar NC (No Anular)
                </button>
                <button
                  type="button"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={submitting}
                  className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default PickupPaymentModal;
